import React, { useState, useEffect } from 'react';
import { Search, Filter, FileText, Calendar, User, CheckCircle, XCircle, Clock, Eye, ChevronUp, ChevronDown, X, MapPin, CreditCard, Building2, Warehouse, Building, Package } from 'lucide-react';

import API_BASE_URL from "../config";

const PRApprovalHistory = () => {
    const [prList, setPRList] = useState([]);
    const [filteredPRs, setFilteredPRs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [prTypeFilter, setPRTypeFilter] = useState('all');
    const [companyFilter, setCompanyFilter] = useState('all');
    const [selectedPR, setSelectedPR] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [prDetails, setPRDetails] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'creationDate', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [destinations, setDestinations] = useState([]);
    const fetchDestinations = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/InventoryDestination`);
            const data = await res.json();
            setDestinations(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching destinations:', error);
        }
    };
    useEffect(() => {
        fetchPRHistory();
        fetchDestinations(); // 👈 dagdag to
    }, []);
    const getInventoryDestinationType = (pr) => {
        const destination = destinations.find(d => d.destinationId === pr.inventoryDestinationId);
        if (!destination) return { type: 'N/A', color: 'gray', icon: null };

        if (destination.asset && !destination.wms && !destination.both) {
            return {
                type: 'Asset',
                color: 'green',
                icon: Building,
                destinationName: destination.destinationName,
                destinationCode: destination.destinationCode
            };
        } else if (destination.wms && !destination.asset && !destination.both) {
            return {
                type: 'WMS',
                color: 'blue',
                icon: Warehouse,
                destinationName: destination.destinationName,
                destinationCode: destination.destinationCode
            };
        } else if (destination.both) {
            return {
                type: 'Both',
                color: 'purple',
                icon: Package,
                destinationName: destination.destinationName,
                destinationCode: destination.destinationCode
            };
        }

        return { type: 'N/A', color: 'gray', icon: null };
    };
    useEffect(() => {
        filterAndSortPRs();
    }, [searchTerm, statusFilter, prTypeFilter, companyFilter, prList, sortConfig]);

    const fetchPRHistory = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/PR_Header`);
            const data = await res.json();

            // Filter status -1, 0, 1, 2, 3
            let filteredData = data.filter(pr => [-1, 0, 1, 2, 3].includes(pr.status));

            // Get current user from localStorage
            const user = JSON.parse(localStorage.getItem("user"));

            // Filter based on position
            if (user.positionId !== "SYSTEM ADMIN") { // 1 = SYSTEM ADMIN
                filteredData = filteredData.filter(pr => pr.createdBy === user.userId);
            }
            // If SYSTEM ADMIN (positionId === 1), show all PRs

            setPRList(filteredData);
            setFilteredPRs(filteredData);
        } catch (error) {
            console.error('Error fetching PR history:', error);
        } finally {
            setLoading(false);
        }
    };
    const filterAndSortPRs = () => {
        let filtered = [...prList];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(pr =>
                pr.prNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pr.remarks?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pr.brands?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(pr => pr.status === parseInt(statusFilter));
        }

        // PR Type filter
        if (prTypeFilter !== 'all') {
            filtered = filtered.filter(pr => pr.prType === parseInt(prTypeFilter));
        }

        // Company filter
        if (companyFilter !== 'all') {
            filtered = filtered.filter(pr => pr.companyId === parseInt(companyFilter));
        }

        // Sort
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle dates
                if (sortConfig.key.includes('Date')) {
                    aValue = new Date(aValue || 0);
                    bValue = new Date(bValue || 0);
                }

                // Handle strings
                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue?.toLowerCase() || '';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        setFilteredPRs(filtered);
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const fetchPRDetails = async (prHeaderId) => {
        try {
            const res = await fetch(`${API_BASE_URL}api/PR_Details/PRHeader/${prHeaderId}`);
            const data = await res.json();
            setPRDetails(Array.isArray(data) ? data : [data]);
        } catch (error) {
            console.error('Error fetching PR details:', error);
            setPRDetails([]);
        }
    };

    const handleViewDetails = (pr) => {
        setSelectedPR(pr);
        fetchPRDetails(pr.id);
        setShowModal(true);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case -1:
                return (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 flex items-center gap-1 w-fit">
                        <XCircle className="w-3 h-3" />
                        Rejected
                    </span>
                );
            case 0:
                return (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 flex items-center gap-1 w-fit">
                        <FileText className="w-3 h-3" />
                        Draft
                    </span>
                );
            case 1:
                return (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 flex items-center gap-1 w-fit">
                        <Clock className="w-3 h-3" />
                        Pending
                    </span>
                );
            case 2:
                return (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                        <CheckCircle className="w-3 h-3" />
                        Approved
                    </span>
                );
            case 3:
                return (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 flex items-center gap-1 w-fit">
                        <CheckCircle className="w-3 h-3" />
                        Completed
                    </span>
                );
            default:
                return (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 w-fit">
                        Unknown
                    </span>
                );
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-PH', {
            style: 'currency',
            currency: 'PHP'
        }).format(amount || 0);
    };

    const getStatusCount = (status) => {
        return prList.filter(pr => pr.status === status).length;
    };

    const getUniqueCompanies = () => {
        return [...new Set(prList.map(pr => pr.companyId))].sort((a, b) => a - b);
    };

    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) {
            return <ChevronUp className="w-4 h-4 text-gray-400 opacity-50" />;
        }
        return sortConfig.direction === 'asc'
            ? <ChevronUp className="w-4 h-4 text-white" />
            : <ChevronDown className="w-4 h-4 text-white" />;
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setPRTypeFilter('all');
        setCompanyFilter('all');
        setSortConfig({ key: 'creationDate', direction: 'desc' });
    };

    // Pagination calculations
    const totalPages = Math.ceil(filteredPRs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentPRs = filteredPRs.slice(startIndex, endIndex);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter, prTypeFilter, companyFilter, sortConfig]);

    const [companyName, setCompanyName] = useState("");

    useEffect(() => {
        if (!selectedPR?.companyId) return;

        const fetchCompany = async () => {
            try {
                const res = await fetch(`http://localhost:7266/api/Company/${selectedPR.companyId}`);
                const data = await res.json();
                setCompanyName(data.companyName || "N/A");
            } catch (error) {
                console.error("Error fetching company:", error);
                setCompanyName("N/A");
            }
        };

        fetchCompany();
    }, [selectedPR?.companyId]);

    const [expenseTypeName, setExpenseTypeName] = useState("");

    useEffect(() => {
        if (!selectedPR?.prType) return;

        const fetchExpenseType = async () => {
            try {
                const res = await fetch(`http://localhost:7266/api/ExpenseType/${selectedPR.prType}`);
                const data = await res.json();
                setExpenseTypeName(data.expenseTypeName || "N/A");
            } catch (error) {
                console.error("Error fetching expense type:", error);
                setExpenseTypeName("N/A");
            }
        };

        fetchExpenseType();
    }, [selectedPR?.prType]);


    // LOCATION NAME
    const [locationName, setLocationName] = useState("");

    // SHIP TO NAME
    const [shipToName, setShipToName] = useState("");


    // 👉 Fetch Location (from /api/Location/{id})
    useEffect(() => {
        if (!selectedPR?.locationId) return;

        const fetchLocation = async () => {
            try {
                const res = await fetch(`http://localhost:7266/api/Location/${selectedPR.locationId}`);
                const data = await res.json();

                setLocationName(data.locationName || "N/A");
            } catch (error) {
                console.error("Error fetching Location:", error);
                setLocationName("N/A");
            }
        };

        fetchLocation();
    }, [selectedPR?.locationId]);


    // 👉 Fetch ShipTo (from /api/ShippingInfo/{id})
    useEffect(() => {
        if (!selectedPR?.shipToId) return;

        const fetchShipTo = async () => {
            try {
                const res = await fetch(`http://localhost:7266/api/ShippingInfo/${selectedPR.shipToId}`);
                const data = await res.json();

                setShipToName(data.shipTo_Location || "N/A");
            } catch (error) {
                console.error("Error fetching ShipTo:", error);
                setShipToName("N/A");
            }
        };

        fetchShipTo();
    }, [selectedPR?.shipToId]);


    const [deptName, setDeptName] = useState("");

    useEffect(() => {
        if (!selectedPR?.chargedToDept) return;

        const fetchDepartment = async () => {
            try {
                const res = await fetch(`http://localhost:7266/api/Department/${selectedPR.chargedToDept}`);
                const data = await res.json();
                setDeptName(data.departmentName || "N/A");
            } catch (error) {
                console.error("Error fetching department:", error);
                setDeptName("N/A");
            }
        };

        fetchDepartment();
    }, [selectedPR?.chargedToDept]);

    return (
        <div className=" from-gray-50 to-gray-100 p-6">
            <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-600 p-3 rounded-lg">
                                <FileText className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">PR Approval History</h1>
                                <p className="text-gray-600 text-sm">View and manage purchase request records</p>
                            </div>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-gray-50 to-slate-50 border-2 border-gray-300 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-700 text-sm font-semibold uppercase">Draft</p>
                                    <p className="text-3xl font-bold text-gray-900">{getStatusCount(0)}</p>
                                </div>
                                <FileText className="w-12 h-12 text-gray-400" />
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-yellow-700 text-sm font-semibold uppercase">Pending</p>
                                    <p className="text-3xl font-bold text-yellow-900">{getStatusCount(1)}</p>
                                </div>
                                <Clock className="w-12 h-12 text-yellow-400" />
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-700 text-sm font-semibold uppercase">Approved</p>
                                    <p className="text-3xl font-bold text-green-900">{getStatusCount(2)}</p>
                                </div>
                                <CheckCircle className="w-12 h-12 text-green-400" />
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-blue-50 to-sky-50 border-2 border-blue-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-700 text-sm font-semibold uppercase">Completed</p>
                                    <p className="text-3xl font-bold text-blue-900">{getStatusCount(3)}</p>
                                </div>
                                <CheckCircle className="w-12 h-12 text-blue-400" />
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-red-700 text-sm font-semibold uppercase">Rejected</p>
                                    <p className="text-3xl font-bold text-red-900">{getStatusCount(-1)}</p>
                                </div>
                                <XCircle className="w-12 h-12 text-red-400" />
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="space-y-4">
                        <div className="flex flex-col md:flex-row gap-4">
                            {/* Search */}
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search by PR Number, Remarks, or Brands..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                                />
                            </div>

                            {/* Status Filter */}
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none appearance-none bg-white min-w-[180px]"
                                >
                                    <option value="all">All Status</option>
                                    <option value="0">Draft</option>
                                    <option value="1">Pending</option>
                                    <option value="2">Approved</option>
                                    <option value="3">Completed</option>
                                    <option value="-1">Rejected</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            {/* PR Type Filter */}
                            <div className="relative flex-1">
                                <label className="text-xs font-semibold text-gray-600 uppercase mb-1 block">PR Type</label>
                                <select
                                    value={prTypeFilter}
                                    onChange={(e) => setPRTypeFilter(e.target.value)}
                                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none appearance-none bg-white"
                                >
                                    <option value="all">All Types</option>
                                    <option value="1">Type 1</option>
                                    <option value="2">Type 2</option>
                                    <option value="3">Type 3</option>
                                </select>
                            </div>

                            {/* Company Filter */}
                            <div className="relative flex-1">
                                <label className="text-xs font-semibold text-gray-600 uppercase mb-1 block">Company</label>
                                <select
                                    value={companyFilter}
                                    onChange={(e) => setCompanyFilter(e.target.value)}
                                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none appearance-none bg-white"
                                >
                                    <option value="all">All Companies</option>
                                    {getUniqueCompanies().map(companyId => (
                                        <option key={companyId} value={companyId}>Company {companyId}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Clear Filters Button */}
                            <button
                                onClick={clearFilters}
                                className="px-6 py-2.5 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors font-semibold flex items-center gap-2"
                            >
                                <X className="w-4 h-4" />
                                Clear Filters
                            </button>
                        </div>

                        {/* Active Filters Display */}
                        {(searchTerm || statusFilter !== 'all' || prTypeFilter !== 'all' || companyFilter !== 'all') && (
                            <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                                <span className="font-semibold">Active filters:</span>
                                {searchTerm && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Search: "{searchTerm}"</span>}
                                {statusFilter !== 'all' && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Status: {
                                    statusFilter === '-1' ? 'Rejected' :
                                        statusFilter === '0' ? 'Draft' :
                                            statusFilter === '1' ? 'Pending' :
                                                statusFilter === '2' ? 'Approved' :
                                                    'Completed'
                                }</span>}
                                {prTypeFilter !== 'all' && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Type: {prTypeFilter}</span>}
                                {companyFilter !== 'all' && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Company: {companyFilter}</span>}
                            </div>
                        )}
                    </div>
                </div>

                {/* PR List */}
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="text-center">
                                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
                                <p className="text-gray-600">Loading PR history...</p>
                            </div>
                        </div>
                    ) : filteredPRs.length === 0 ? (
                        <div className="text-center py-20">
                            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg">No purchase requests found</p>
                            <p className="text-gray-400 text-sm mt-2">Try adjusting your filters</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gradient-to-r from-blue-600 to-blue-600 text-white">
                                    <tr>
                                        <th
                                            className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                                            onClick={() => handleSort('prNo')}
                                        >
                                            <div className="flex items-center gap-2">
                                                PR Number
                                                <SortIcon columnKey="prNo" />
                                            </div>
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-bold uppercase">
                                            Inventory Destination
                                        </th>
                                        <th
                                            className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                                            onClick={() => handleSort('createdBy')}
                                        >
                                            <div className="flex items-center gap-2">
                                                Created By
                                                <SortIcon columnKey="createdBy" />
                                            </div>
                                        </th>
                                        <th
                                            className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                                            onClick={() => handleSort('creationDate')}
                                        >
                                            <div className="flex items-center gap-2">
                                                Creation Date
                                                <SortIcon columnKey="creationDate" />
                                            </div>
                                        </th>
                                        <th
                                            className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                                            onClick={() => handleSort('dateNeeded')}
                                        >
                                            <div className="flex items-center gap-2">
                                                Date Needed
                                                <SortIcon columnKey="dateNeeded" />
                                            </div>
                                        </th>

                                        <th
                                            className="px-6 py-4 text-center text-xs font-bold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                                            onClick={() => handleSort('status')}
                                        >
                                            <div className="flex items-center justify-center gap-2">
                                                Status
                                                <SortIcon columnKey="status" />
                                            </div>
                                        </th>
                                        <th className="px-6 py-4 text-center text-xs font-bold uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {currentPRs.map((pr, index) => (
                                        <tr
                                            key={pr.id}
                                            className={`hover:bg-blue-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                                }`}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-blue-600" />
                                                    <span className="font-bold text-blue-700">{pr.prNo}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    {(() => {
                                                        const destInfo = getInventoryDestinationType(pr);
                                                        const IconComponent = destInfo.icon;
                                                        return (
                                                            <>
                                                                {IconComponent && <IconComponent className={`w-4 h-4 text-${destInfo.color}-600`} />}
                                                                <div>
                                                                    <span className={`px-3 py-1 rounded-full text-xs font-bold bg-${destInfo.color}-100 text-${destInfo.color}-800`}>
                                                                        {destInfo.type}
                                                                    </span>
                                                                    {destInfo.destinationName && (
                                                                        <p className="text-xs text-gray-600 mt-1">
                                                                            {destInfo.destinationName} ({destInfo.destinationCode})
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <User className="w-4 h-4 text-gray-500" />
                                                    <span className="text-sm text-gray-700">User {pr.createdBy}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-gray-500" />
                                                    <span className="text-sm text-gray-700">{formatDate(pr.creationDate)}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-700">{formatDate(pr.dateNeeded)}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex justify-center">
                                                    {getStatusBadge(pr.status)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <button
                                                    onClick={() => handleViewDetails(pr)}
                                                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-semibold"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Results Counter */}
                    {!loading && (
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div className="flex items-center gap-4">
                                    <p className="text-sm text-gray-600">
                                        Showing <span className="font-bold text-gray-900">{startIndex + 1}</span> to <span className="font-bold text-gray-900">{Math.min(endIndex, filteredPRs.length)}</span> of <span className="font-bold text-gray-900">{filteredPRs.length}</span> purchase requests
                                    </p>

                                    {/* Items per page selector */}
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm text-gray-600">Show:</label>
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                                        >
                                            First
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                                        >
                                            Previous
                                        </button>

                                        {/* Page Numbers */}
                                        <div className="flex items-center gap-1">
                                            {[...Array(totalPages)].map((_, idx) => {
                                                const pageNum = idx + 1;
                                                // Show first page, last page, current page, and pages around current
                                                if (
                                                    pageNum === 1 ||
                                                    pageNum === totalPages ||
                                                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                                ) {
                                                    return (
                                                        <button
                                                            key={pageNum}
                                                            onClick={() => setCurrentPage(pageNum)}
                                                            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                                                                ? 'bg-blue-600 text-white'
                                                                : 'border border-gray-300 hover:bg-gray-100'
                                                                }`}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    );
                                                } else if (
                                                    pageNum === currentPage - 2 ||
                                                    pageNum === currentPage + 2
                                                ) {
                                                    return <span key={pageNum} className="px-2 text-gray-500">...</span>;
                                                }
                                                return null;
                                            })}
                                        </div>

                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                                        >
                                            Next
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(totalPages)}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                                        >
                                            Last
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Enhanced Details Modal */}
            {showModal && selectedPR && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
                        {/* Modal Header - Fixed */}
                        <div className="bg-gradient-to-r from-blue-600 via-blue-600 to-blue-700 text-white px-8 py-6 flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                                        <FileText className="w-7 h-7" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold tracking-tight">Purchase Request Details</h3>
                                        <p className="text-blue-100 text-sm mt-1 flex items-center gap-2">
                                            <span className="bg-white/20 px-3 py-1 rounded-full font-semibold">{selectedPR.prNo}</span>
                                            {getStatusBadge(selectedPR.status)}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="bg-white/10 hover:bg-white/20 rounded-xl p-3 transition-all duration-200 hover:rotate-90"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        {/* Modal Body - Scrollable */}
                        <div className="overflow-y-auto flex-1 bg-gray-50">
                            <div className="p-8 space-y-6">
                                {/* Overview Cards */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="bg-white/20 p-2 rounded-lg">
                                                <User className="w-5 h-5" />
                                            </div>
                                            <p className="text-xs uppercase font-bold tracking-wider opacity-90">Created By</p>
                                        </div>
                                        <p className="text-2xl font-bold">User {selectedPR.createdBy}</p>
                                        <p className="text-xs mt-2 opacity-75">{formatDateTime(selectedPR.creationDate)}</p>
                                    </div>

                                    <div className="bg-gradient-to-br from-purple-500 to-pink-600 text-white p-6 rounded-xl shadow-lg">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="bg-white/20 p-2 rounded-lg">
                                                <Building2 className="w-5 h-5" />
                                            </div>
                                            <p className="text-xs uppercase font-bold tracking-wider opacity-90">Company</p>
                                        </div>
                                        <p className="text-2xl font-bold"> {companyName}</p>
                                        <p className="text-xs mt-2 opacity-75">{expenseTypeName}</p>
                                    </div>

                                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-6 rounded-xl shadow-lg">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="bg-white/20 p-2 rounded-lg">
                                                <Calendar className="w-5 h-5" />
                                            </div>
                                            <p className="text-xs uppercase font-bold tracking-wider opacity-90">Date Needed</p>
                                        </div>
                                        <p className="text-2xl font-bold">{formatDate(selectedPR.dateNeeded)}</p>
                                        <p className="text-xs mt-2 opacity-75">Submitted: {formatDateTime(selectedPR.dateSubmittedForApproval)}</p>
                                    </div>
                                </div>

                                {/* Details Section */}
                                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                                    <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4">
                                        <h4 className="text-white font-bold text-sm uppercase tracking-wider">Request Information</h4>
                                    </div>
                                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div className="space-y-1">
                                            <p className="text-xs text-gray-500 uppercase font-semibold flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                Location
                                            </p>
                                            <p className="text-sm font-bold text-gray-900">{locationName || "N/A"}</p>
                                        </div>

                                        <div className="space-y-1">
                                            <p className="text-xs text-gray-500 uppercase font-semibold flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                Ship To
                                            </p>
                                            <p className="text-sm font-bold text-gray-900">{shipToName || "N/A"}</p>
                                        </div>

                                        <div className="space-y-1">
                                            <p className="text-xs text-gray-500 uppercase font-semibold flex items-center gap-1">
                                                Expense Type
                                            </p>
                                            <p className="text-sm font-bold text-gray-900">{expenseTypeName}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs text-gray-500 uppercase font-semibold">Charged To User</p>
                                            <p className="text-sm font-bold text-gray-900">User {selectedPR.chargeToUser}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs text-gray-500 uppercase font-semibold">Charged To Dept</p>
                                            <p className="text-sm font-bold text-gray-900">Dept {deptName}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs text-gray-500 uppercase font-semibold">Approver Level</p>
                                            <p className="text-sm font-bold text-gray-900">{selectedPR.approverLevel}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs text-gray-500 uppercase font-semibold">Payment Method</p>
                                            <p className="text-sm font-bold text-gray-900">{selectedPR.paymentMethodId || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1 col-span-2">
                                            <p className="text-xs text-gray-500 uppercase font-semibold">Annual Budget</p>
                                            <p className="text-sm font-bold text-gray-900">
                                                {selectedPR.partOfAnnualBudget === 1 ? '✓ Yes' : '✗ No'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Info */}
                                <div className="grid grid-cols-1 gap-4">
                                    {selectedPR.brands && (
                                        <div className="bg-white border-l-4 border-purple-500 rounded-lg p-5 shadow-sm">
                                            <p className="text-xs text-purple-700 uppercase font-bold mb-2 flex items-center gap-2">
                                                <span className="bg-purple-100 p-1 rounded">📦</span> Brands
                                            </p>
                                            <p className="text-sm text-gray-800 font-medium">{selectedPR.brands}</p>
                                        </div>
                                    )}

                                    {selectedPR.chargingVisa && (
                                        <div className="bg-white border-l-4 border-blue-500 rounded-lg p-5 shadow-sm">
                                            <p className="text-xs text-blue-700 uppercase font-bold mb-2 flex items-center gap-2">
                                                <CreditCard className="w-4 h-4" /> Charging Visa
                                            </p>
                                            <p className="text-sm text-gray-800 font-medium">{selectedPR.chargingVisa}</p>
                                        </div>
                                    )}

                                    {selectedPR.remarks && (
                                        <div className="bg-white border-l-4 border-amber-500 rounded-lg p-5 shadow-sm">
                                            <p className="text-xs text-amber-700 uppercase font-bold mb-2 flex items-center gap-2">
                                                <span className="bg-amber-100 p-1 rounded">💬</span> Remarks
                                            </p>
                                            <p className="text-sm text-gray-800 leading-relaxed">{selectedPR.remarks}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Items Table */}
                                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                                    <div className="bg-gradient-to-r from-blue-600 to-blue-600 px-6 py-4">
                                        <h4 className="text-white font-bold text-sm uppercase tracking-wider flex items-center gap-2">
                                            <FileText className="w-4 h-4" />
                                            Items List
                                        </h4>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-100 border-b-2 border-gray-300">
                                                <tr>
                                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">#</th>
                                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Description</th>
                                                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Quantity</th>
                                                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">UOM</th>
                                                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Unit Price</th>
                                                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Total Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {prDetails.length > 0 ? (
                                                    prDetails.map((item, index) => (
                                                        <tr key={item.id || index} className="hover:bg-blue-50 transition-colors">
                                                            <td className="px-6 py-4">
                                                                <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-bold text-sm">
                                                                    {index + 1}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <p className="text-sm font-bold text-gray-900">{item.itemDescription}</p>
                                                                {item.sizeMeasurements && (
                                                                    <p className="text-xs text-gray-500 mt-1 italic">{item.sizeMeasurements}</p>
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-4 text-center">
                                                                <span className="inline-block bg-gray-100 px-3 py-1 rounded-full text-sm font-bold text-gray-800">
                                                                    {item.qty}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 text-center text-sm font-medium text-gray-700">{item.uom}</td>
                                                            <td className="px-6 py-4 text-right text-sm font-semibold text-gray-800">
                                                                {formatCurrency(item.unitPrice)}
                                                            </td>
                                                            <td className="px-6 py-4 text-right">
                                                                <span className="text-base font-bold text-blue-700">
                                                                    {formatCurrency(item.costEstimate)}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="px-6 py-12 text-center">
                                                            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                                            <p className="text-gray-500 font-medium">No items found for this PR</p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {prDetails.length > 0 && (
                                        <div className="to-blue-600 px-8 py-5">
                                            <div className="flex justify-between items-center">
                                                <div className="text-black">
                                                    <p className="text-xs uppercase tracking-wider opacity-90 mb-1">Grand Total</p>
                                                    <p className="text-sm opacity-75">{prDetails.length} item{prDetails.length !== 1 ? 's' : ''}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-3xl font-bold text-black drop-shadow-lg">
                                                        {formatCurrency(prDetails.reduce((sum, item) => sum + (item.costEstimate || 0), 0))}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PRApprovalHistory;